import { api } from './api';
import { API_CONFIG } from '../config/api';
import { 
    TestItem, 
    TestItemCreate, 
    TestItemUpdate, 
    TestItemFilter, 
    TestItemType,
    TestItemPriority,
    TestItemSeverity,
    TestItemStatus,
    TestItemStep,
    TestItemCheckItem,
    SharedStep,
    CustomFieldDefinition,
    CustomFieldValue,
    TestData,
    Attachment,
    Folder,
    TestSuite,
    FolderCreate,
    FolderUpdate,
    TestItemVersion
} from '../types/testItem';

// Add request interceptor to include auth token
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

export const testItemService = {
    // Test Items
    async getTestItems(filter: TestItemFilter): Promise<TestItem[]> {
        try {
            console.log('Fetching test items with filter:', filter);
            const response = await api.get('/test-items', { params: filter });
            console.log('Test items fetched successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching test items:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to fetch test items');
        }
    },

    async getTestItem(projectId: number, id: number): Promise<TestItem> {
        try {
            console.log(`Fetching test item ${id} for project ${projectId}...`);
            const response = await api.get(`/projects/${projectId}/test-items/${id}`);
            console.log('Test item fetched successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching test item:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to fetch test item');
        }
    },

    async createTestItem(data: TestItemCreate): Promise<TestItem> {
        try {
            console.log('Creating test item with data:', data);
            const response = await api.post('/test-items', data);
            console.log('Test item created successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error creating test item:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to create test item');
        }
    },

    async updateTestItem(id: number, data: TestItemUpdate): Promise<TestItem> {
        try {
            console.log(`Updating test item ${id} with data:`, data);
            const response = await api.put(`/test-items/${id}`, data);
            console.log('Test item updated successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error updating test item:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to update test item');
        }
    },

    async deleteTestItem(projectId: number, id: number): Promise<void> {
        try {
            console.log(`Deleting test item ${id} from project ${projectId}...`);
            await api.delete(`/projects/${projectId}/test-items/${id}`);
            console.log('Test item deleted successfully');
        } catch (error: any) {
            console.error('Error deleting test item:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to delete test item');
        }
    },

    // Versions
    async getTestItemVersions(projectId: number, testItemId: number): Promise<TestItemVersion[]> {
        try {
            console.log(`Fetching versions for test item ${testItemId} in project ${projectId}...`);
            const response = await api.get(`/projects/${projectId}/test-items/${testItemId}/versions`);
            console.log('Test item versions fetched successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching test item versions:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to fetch test item versions');
        }
    },

    async getTestItemVersion(projectId: number, testItemId: number, versionId: number) {
        try {
            console.log(`Fetching version ${versionId} for test item ${testItemId} in project ${projectId}...`);
            const response = await api.get(`/projects/${projectId}/test-items/${testItemId}/versions/${versionId}`);
            console.log('Test item version fetched successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching test item version:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to fetch test item version');
        }
    },

    async revertToVersion(projectId: number, testItemId: number, versionId: number): Promise<TestItem> {
        try {
            console.log(`Reverting test item ${testItemId} to version ${versionId} in project ${projectId}...`);
            const response = await api.post(`/projects/${projectId}/test-items/${testItemId}/versions/${versionId}/revert`);
            console.log('Test item reverted successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error reverting to version:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to revert to version');
        }
    },

    // Shared Steps
    async getSharedSteps(projectId: number): Promise<SharedStep[]> {
        try {
            console.log(`Fetching shared steps for project ${projectId}...`);
            const response = await api.get(`/projects/${projectId}/shared-steps`);
            console.log('Shared steps fetched successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching shared steps:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            
            if (error.response?.status === 404) {
                throw new Error('Project not found');
            }
            
            if (error.message === 'Network Error') {
                throw new Error('Failed to connect to the server. Please check your internet connection.');
            }
            
            throw new Error(error.response?.data?.detail || 'Failed to fetch shared steps');
        }
    },

    async createSharedStep(projectId: number, data: Omit<SharedStep, 'id' | 'createdAt'>) {
        try {
            console.log(`Creating shared step for project ${projectId}...`);
            const response = await api.post(`/projects/${projectId}/shared-steps`, data);
            console.log('Shared step created successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error creating shared step:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to create shared step');
        }
    },

    async updateSharedStep(projectId: number, stepId: number, data: Partial<SharedStep>) {
        try {
            console.log(`Updating shared step ${stepId} for project ${projectId}...`);
            const response = await api.put(`/projects/${projectId}/shared-steps/${stepId}`, data);
            console.log('Shared step updated successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error updating shared step:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to update shared step');
        }
    },

    async deleteSharedStep(projectId: number, stepId: number) {
        try {
            console.log(`Deleting shared step ${stepId} for project ${projectId}...`);
            await api.delete(`/projects/${projectId}/shared-steps/${stepId}`);
            console.log('Shared step deleted successfully');
        } catch (error: any) {
            console.error('Error deleting shared step:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to delete shared step');
        }
    },

    // Folders
    async getFolders(projectId: number): Promise<Folder[]> {
        try {
            console.log(`Fetching folders for project ${projectId}...`);
            const response = await api.get(`/projects/${projectId}/folders`);
            console.log('Folders fetched successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching folders:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to fetch folders');
        }
    },

    async getFolder(projectId: number, folderId: number) {
        try {
            console.log(`Fetching folder ${folderId} for project ${projectId}...`);
            const response = await api.get(`/projects/${projectId}/folders/${folderId}`);
            console.log('Folder fetched successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching folder:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to fetch folder');
        }
    },

    async createFolder(projectId: number, data: FolderCreate) {
        try {
            console.log(`Creating folder for project ${projectId}...`);
            const response = await api.post(`/projects/${projectId}/folders`, data);
            console.log('Folder created successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error creating folder:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to create folder');
        }
    },

    async updateFolder(projectId: number, folderId: number, data: FolderUpdate) {
        try {
            console.log(`Updating folder ${folderId} for project ${projectId}...`);
            const response = await api.put(`/projects/${projectId}/folders/${folderId}`, data);
            console.log('Folder updated successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error updating folder:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to update folder');
        }
    },

    async deleteFolder(projectId: number, folderId: number) {
        try {
            console.log(`Deleting folder ${folderId} for project ${projectId}...`);
            await api.delete(`/projects/${projectId}/folders/${folderId}`);
            console.log('Folder deleted successfully');
        } catch (error: any) {
            console.error('Error deleting folder:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to delete folder');
        }
    },

    // Test Suites
    async getTestSuites(projectId: number) {
        try {
            console.log(`Fetching test suites for project ${projectId}...`);
            const response = await api.get(`/projects/${projectId}/test-suites`);
            console.log('Test suites fetched successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching test suites:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to fetch test suites');
        }
    },

    async createTestSuite(projectId: number, data: Omit<TestSuite, 'id'>) {
        try {
            console.log(`Creating test suite for project ${projectId}...`);
            const response = await api.post(`/projects/${projectId}/test-suites`, data);
            console.log('Test suite created successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error creating test suite:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to create test suite');
        }
    },

    async updateTestSuite(projectId: number, suiteId: number, data: Partial<TestSuite>) {
        try {
            console.log(`Updating test suite ${suiteId} for project ${projectId}...`);
            const response = await api.put(`/projects/${projectId}/test-suites/${suiteId}`, data);
            console.log('Test suite updated successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error updating test suite:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to update test suite');
        }
    },

    async deleteTestSuite(projectId: number, suiteId: number) {
        try {
            console.log(`Deleting test suite ${suiteId} for project ${projectId}...`);
            await api.delete(`/projects/${projectId}/test-suites/${suiteId}`);
            console.log('Test suite deleted successfully');
        } catch (error: any) {
            console.error('Error deleting test suite:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to delete test suite');
        }
    },

    // Custom Fields
    async getCustomFields(projectId: number) {
        try {
            console.log(`Fetching custom fields for project ${projectId}...`);
            const response = await api.get(`/projects/${projectId}/custom-fields`);
            console.log('Custom fields fetched successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching custom fields:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to fetch custom fields');
        }
    },

    async createCustomField(projectId: number, data: Omit<CustomFieldDefinition, 'id'>) {
        try {
            console.log(`Creating custom field for project ${projectId}...`);
            const response = await api.post(`/projects/${projectId}/custom-fields`, data);
            console.log('Custom field created successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error creating custom field:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to create custom field');
        }
    },

    async updateCustomField(projectId: number, fieldId: number, data: Partial<CustomFieldDefinition>) {
        try {
            console.log(`Updating custom field ${fieldId} for project ${projectId}...`);
            const response = await api.put(`/projects/${projectId}/custom-fields/${fieldId}`, data);
            console.log('Custom field updated successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error updating custom field:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to update custom field');
        }
    },

    async deleteCustomField(projectId: number, fieldId: number) {
        try {
            console.log(`Deleting custom field ${fieldId} for project ${projectId}...`);
            await api.delete(`/projects/${projectId}/custom-fields/${fieldId}`);
            console.log('Custom field deleted successfully');
        } catch (error: any) {
            console.error('Error deleting custom field:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to delete custom field');
        }
    },

    // Test Data
    async getTestData(projectId: number) {
        try {
            console.log(`Fetching test data for project ${projectId}...`);
            const response = await api.get(`/projects/${projectId}/test-data`);
            console.log('Test data fetched successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching test data:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to fetch test data');
        }
    },

    async createTestData(projectId: number, data: Omit<TestData, 'id'>) {
        try {
            console.log(`Creating test data for project ${projectId}...`);
            const response = await api.post(`/projects/${projectId}/test-data`, data);
            console.log('Test data created successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error creating test data:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to create test data');
        }
    },

    async updateTestData(projectId: number, dataId: number, data: Partial<TestData>) {
        try {
            console.log(`Updating test data ${dataId} for project ${projectId}...`);
            const response = await api.put(`/projects/${projectId}/test-data/${dataId}`, data);
            console.log('Test data updated successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error updating test data:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to update test data');
        }
    },

    async deleteTestData(projectId: number, dataId: number) {
        try {
            console.log(`Deleting test data ${dataId} for project ${projectId}...`);
            await api.delete(`/projects/${projectId}/test-data/${dataId}`);
            console.log('Test data deleted successfully');
        } catch (error: any) {
            console.error('Error deleting test data:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to delete test data');
        }
    },

    // Attachments
    async getAttachments(projectId: number, testItemId: number) {
        try {
            console.log(`Fetching attachments for test item ${testItemId} in project ${projectId}...`);
            const response = await api.get(`/projects/${projectId}/test-items/${testItemId}/attachments`);
            console.log('Attachments fetched successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching attachments:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to fetch attachments');
        }
    },

    async uploadAttachmentToTestItem(projectId: number, testItemId: number, file: File) {
        try {
            console.log(`Uploading attachment for test item ${testItemId} in project ${projectId}...`);
            const formData = new FormData();
            formData.append('file', file);
            const response = await api.post(`/projects/${projectId}/test-items/${testItemId}/attachments`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            console.log('Attachment uploaded successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error uploading attachment:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to upload attachment');
        }
    },

    async deleteAttachmentFromTestItem(projectId: number, testItemId: number, attachmentId: number) {
        try {
            console.log(`Deleting attachment ${attachmentId} from test item ${testItemId} in project ${projectId}...`);
            await api.delete(`/projects/${projectId}/test-items/${testItemId}/attachments/${attachmentId}`);
            console.log('Attachment deleted successfully');
        } catch (error: any) {
            console.error('Error deleting attachment:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to delete attachment');
        }
    },

    // Labels
    async getLabels(projectId: number) {
        try {
            console.log(`Fetching labels for project ${projectId}...`);
            const response = await api.get(`/projects/${projectId}/labels`);
            console.log('Labels fetched successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching labels:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to fetch labels');
        }
    },

    // Steps
    async getSteps(projectId: number, testItemId: number) {
        try {
            console.log(`Fetching steps for test item ${testItemId} in project ${projectId}...`);
            const response = await api.get(`/projects/${projectId}/test-items/${testItemId}/steps`);
            console.log('Steps fetched successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching steps:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to fetch steps');
        }
    },

    async createStep(projectId: number, testItemId: number, data: { action: string; expected_result: string }) {
        try {
            console.log(`Creating step for test item ${testItemId} in project ${projectId}...`);
            const response = await api.post(`/projects/${projectId}/test-items/${testItemId}/steps`, data);
            console.log('Step created successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error creating step:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to create step');
        }
    },

    async updateStep(projectId: number, testItemId: number, stepId: number, data: { action: string; expected_result: string }) {
        try {
            console.log(`Updating step ${stepId} for test item ${testItemId} in project ${projectId}...`);
            const response = await api.put(`/projects/${projectId}/test-items/${testItemId}/steps/${stepId}`, data);
            console.log('Step updated successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error updating step:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to update step');
        }
    },

    async deleteStep(projectId: number, testItemId: number, stepId: number) {
        try {
            console.log(`Deleting step ${stepId} for test item ${testItemId} in project ${projectId}...`);
            await api.delete(`/projects/${projectId}/test-items/${testItemId}/steps/${stepId}`);
            console.log('Step deleted successfully');
        } catch (error: any) {
            console.error('Error deleting step:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to delete step');
        }
    },

    // Check Items
    async getCheckItems(projectId: number, testItemId: number) {
        try {
            console.log(`Fetching check items for test item ${testItemId} in project ${projectId}...`);
            const response = await api.get(`/projects/${projectId}/test-items/${testItemId}/check-items`);
            console.log('Check items fetched successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching check items:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to fetch check items');
        }
    },

    async createCheckItem(projectId: number, testItemId: number, data: { description: string }) {
        try {
            console.log(`Creating check item for test item ${testItemId} in project ${projectId}...`);
            const response = await api.post(`/projects/${projectId}/test-items/${testItemId}/check-items`, data);
            console.log('Check item created successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error creating check item:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to create check item');
        }
    },

    async updateCheckItem(projectId: number, testItemId: number, checkItemId: number, data: { description: string; is_checked?: boolean }) {
        try {
            console.log(`Updating check item ${checkItemId} for test item ${testItemId} in project ${projectId}...`);
            const response = await api.put(`/projects/${projectId}/test-items/${testItemId}/check-items/${checkItemId}`, data);
            console.log('Check item updated successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error updating check item:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to update check item');
        }
    },

    async deleteCheckItem(projectId: number, testItemId: number, checkItemId: number) {
        try {
            console.log(`Deleting check item ${checkItemId} for test item ${testItemId} in project ${projectId}...`);
            await api.delete(`/projects/${projectId}/test-items/${testItemId}/check-items/${checkItemId}`);
            console.log('Check item deleted successfully');
        } catch (error: any) {
            console.error('Error deleting check item:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to delete check item');
        }
    },

    // History
    async getTestItemHistory(projectId: number, testItemId: number) {
        try {
            console.log(`Fetching history for test item ${testItemId} in project ${projectId}...`);
            const response = await api.get(`/projects/${projectId}/test-items/${testItemId}/history`);
            console.log('Test item history fetched successfully:', response.data);
            return response.data;
        } catch (error: any) {
            console.error('Error fetching test item history:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data,
                config: error.config
            });
            throw new Error(error.response?.data?.detail || 'Failed to fetch test item history');
        }
    }
}; 